import { Avatar, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { fDateTime } from 'src/utils/format-time';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { deleteNote } from 'src/services/role.service';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import FilesPreview from './files-preview';
import { useCandidateProfileContext } from '../hooks/use-candidate-profile-context';
import AddCommentBox from './add-comment-box';

function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name),
      width: 32,
      height: 32,
    },
    children: `${name.charAt(0).toUpperCase()}`,
  };
}

export default function CommentBox({ comment }) {
  // hooks
  const editPopover = usePopover();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const orgId = getOrganizationId(user);
  const { jobId, refetchWithoutLoading } = useCandidateProfileContext();

  // state
  const [mode, setMode] = useState('VIEW');

  // functions
  const onDelete = async () => {
    try {
      const payload = comment.candidate_note_id;

      const response = await deleteNote(orgId, jobId, comment.candidate_note_id, payload);

      if (response.success) {
        enqueueSnackbar('Comment deleted successfully');
        refetchWithoutLoading();
      } else throw new Error(response.message);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Failed to delete comment', { variant: 'error' });
    }
  };

  return (
    <>
      {mode === 'VIEW' && (
        <Stack spacing="8px">
          <Stack direction="row" width="100%" spacing="8px">
            {comment?.creator_name ? (
              <Avatar {...stringAvatar(`${comment?.creator_name}`)} />
            ) : (
              <Avatar />
            )}
            <Stack direction="column" width="100%" spacing="8px">
              <Stack direction="column" width="100%">
                <Stack
                  direction="row"
                  sx={{
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    bgcolor: 'background.primaryGrey3',
                    px: '12px',
                    py: '8px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption" color="content.primaryBlack2">
                    {comment?.creator_name}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing="8px">
                    <Typography variant="caption" color="content.primaryBlack3">
                      {fDateTime(comment?.created_at)}
                    </Typography>
                    <IconButton onClick={editPopover.onOpen}>
                      <Iconify icon="tabler:dots-vertical" />
                    </IconButton>
                    <CustomPopover
                      open={editPopover.open}
                      sx={{ width: '200px' }}
                      onClose={editPopover.onClose}
                    >
                      <MenuItem
                        onClick={() => {
                          setMode('EDIT');
                          editPopover.onClose();
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          onDelete();
                          editPopover.onClose();
                        }}
                      >
                        Delete
                      </MenuItem>
                    </CustomPopover>
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                    bgcolor: 'background.primaryGrey2',
                    px: '12px',
                    py: '8px',
                  }}
                >
                  <Typography variant="body2" >
                    {comment?.note_text.split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </Typography>
                </Stack>
              </Stack>
              <FilesPreview files={comment?.attachments || []} mode={mode} />
            </Stack>
          </Stack>
        </Stack>
      )}
      {mode === 'EDIT' && (
        <AddCommentBox comment={comment} mode={mode} setMode={setMode} />
      )}
    </>
  );
}

CommentBox.propTypes = {
  comment: PropTypes.object.isRequired,
};
